import { useEffect, useMemo } from "react";
import classNames from "classnames";
import { useGlobal } from "reactn";
import { Link } from "react-router-dom";
import { usePageTitleAndDesc } from "@ugg/shared/utils/seo-helpers";
import { getRiotAssetsContext, isHiddenChampion } from "@outplayed/riot-assets";
import { useUserState } from "@ugg/shared/api/requests/accounts/user-state";
import { generateBuildAndRunes } from "@ugg/shared/utils/filter-preference-helpers";
import { getChampionBuildUrl } from "@ugg/shared/routes/app-routes";

export function ChampionHome({ className }: { className?: string }) {
  const getPageTitleAndDesc = usePageTitleAndDesc();
  const championLinks = useChampionLinks();

  useEffect(() => {
    window && window.scrollTo(0, 0);
  }, []);

  return (
    <div className={classNames("w-full", className)}>
      {getPageTitleAndDesc("champions")}
      <div
        className={`
        grid gap-[18px] max-sm:gap-[12px]
        grid-cols-[repeat(auto-fill,minmax(100px,1fr))] [&>*]:max-w-[100px] 
        max-sm:grid-cols-[repeat(auto-fill,minmax(80px,1fr))] max-sm:[&>*]:max-w-[80px] 
        max-xs:grid-cols-[repeat(auto-fill,minmax(60px,1fr))] max-xs:[&>*]:max-w-[60px] 
        p-[18px] max-sm:p-[12px] rounded-[3px] bg-purple-400
      `}
      >
        {championLinks}
      </div>
    </div>
  );
}

function useChampionLinks() {
  const { getNormalizedChampionName, getChampionImg, useChampionMini } = getRiotAssetsContext();
  const [language] = useGlobal("language");
  const { data } = useUserState();
  const { filterBuildsAndRunes } = data?.getState?.filterDefaults || {};
  const { data: championMini } = useChampionMini();

  const sortedChampions = useMemo(() => {
    return Object.values(championMini || {})
      .filter((champion) => !isHiddenChampion(champion))
      .map((champion) => ({
        name: champion.name,
        id: champion.key,
      }))
      .sort((a, b) => {
        return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      });
  }, [championMini]);

  return useMemo(() => {
    return sortedChampions.map((champion) => {
      const simplifiedChampionName = getNormalizedChampionName(champion.id);

      return (
        <Link
          className="group w-full mx-auto"
          key={champion.id}
          to={{
            pathname: getChampionBuildUrl(simplifiedChampionName, { language }),
            search: process.env.UGG_PUBLIC_PLATFORM !== "app" ? generateBuildAndRunes(filterBuildsAndRunes || {}) : "",
          }}
        >
          <div className="flex items-center justify-center aspect-square rounded-[3px] overflow-hidden">
            <div className="flex items-center justify-center flex-none w-[115%] overflow-hidden">
              <img
                className="w-full scale-[0.9] group-hover:scale-[.95] transition-[transform] duration-[250ms]"
                src={getChampionImg(champion.id)}
                alt={champion.name}
              />
            </div>
          </div>
          <div className="group-hover:font-bold mt-[8px] text-[12px] text-white font-medium truncate">{champion.name}</div>
        </Link>
      );
    });
  }, [sortedChampions, filterBuildsAndRunes]);
}
